import React, { useEffect, useMemo, useState } from "react";
import { PrintButton } from "../components/PrintButton";
import { getMigrationSites, getLicenses, getCertificates, getE911, getRooms, getHealthChecks, getHealthCheck, getReadiness, setReadiness } from "../lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  LayoutPanelLeft, ChevronRight, Cloud, Siren, MonitorPlay, ShieldAlert, KeyRound, MapPin, Gauge, ChevronDown,
} from "lucide-react";

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  return Math.ceil((d - new Date().setHours(0, 0, 0, 0)) / 86400000);
}
// The bit of a name before an em-dash / hyphen suffix ("Site 1 — HQ" -> "Site 1").
const baseName = (n) => (n || "").split(/[—-]/)[0].trim();

const MIG_STATUS = {
  pending: { label: "Pending", cls: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
  onboarded: { label: "Onboarded", cls: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
  cutover: { label: "Cutover", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
};
const E911_STATUS = {
  compliant: { label: "Compliant", cls: "text-emerald-400" },
  review: { label: "Needs Review", cls: "text-amber-400" },
  pending: { label: "Pending", cls: "text-blue-400" },
  "non-compliant": { label: "Non-Compliant", cls: "text-red-400" },
};
const ROOM_STATUS = {
  registered: "text-emerald-400", offline: "text-red-400", issue: "text-amber-400", pending: "text-blue-400",
};

export default function Site360() {
  const [sites, setSites] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [certs, setCerts] = useState([]);
  const [e911, setE911] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [cloudChecks, setCloudChecks] = useState([]);
  const [readiness, setReadinessState] = useState({});
  const [openCheck, setOpenCheck] = useState(null);
  const [selId, setSelId] = useState("");

  useEffect(() => {
    getMigrationSites().then((d) => { setSites(d); if (d[0]) setSelId(d[0].id); }).catch(() => {});
    getLicenses().then(setLicenses).catch(() => {});
    getCertificates().then(setCerts).catch(() => {});
    getE911().then(setE911).catch(() => {});
    getRooms().then(setRooms).catch(() => {});
    // Load the Cloud Readiness health checks (with their steps) once.
    getHealthChecks().then((list) => {
      const cloud = list.filter((h) => h.category === "Cloud Readiness");
      Promise.all(cloud.map((h) => getHealthCheck(h.id))).then(setCloudChecks).catch(() => {});
    }).catch(() => {});
  }, []);

  // Per-site readiness results refresh whenever the selected site changes.
  useEffect(() => {
    if (selId) getReadiness(selId).then(setReadinessState).catch(() => setReadinessState({}));
  }, [selId]);

  const scoreFor = (checkId, steps) => {
    const res = readiness[checkId] || {};
    let pass = 0, fail = 0;
    steps.forEach((s) => {
      if (res[s.id] === "pass") pass++;
      else if (res[s.id] === "fail") fail++;
    });
    const scored = pass + fail;
    return { pass, fail, scored, total: steps.length, pct: scored ? Math.round((pass / scored) * 100) : null };
  };

  const mark = async (checkId, stepId, status) => {
    // Toggle off if the same status is clicked again (removes it -> back to unset via 'na').
    const current = (readiness[checkId] || {})[stepId];
    const next = current === status ? "na" : status;
    setReadinessState((prev) => ({ ...prev, [checkId]: { ...(prev[checkId] || {}), [stepId]: next } }));
    try { await setReadiness(selId, { check_id: checkId, step_id: stepId, status: next }); }
    catch { /* keep optimistic */ }
  };

  const site = useMemo(() => sites.find((s) => s.id === selId), [sites, selId]);

  const siteE911 = useMemo(
    () => (site ? e911.find((r) => baseName(r.name) === baseName(site.name)) : null),
    [site, e911]
  );
  const siteRooms = useMemo(
    () => (site ? rooms.filter((r) => (r.location || "").includes(site.name)) : []),
    [site, rooms]
  );

  // Estate-wide context (licenses/certs are not per-site tagged).
  const certExpired = certs.filter((c) => { const d = daysUntil(c.expires); return d !== null && d < 0; }).length;
  const certSoon = certs.filter((c) => { const d = daysUntil(c.expires); return d !== null && d >= 0 && d <= 90; }).length;
  const licEntitled = licenses.reduce((a, l) => a + (l.entitled || 0), 0);
  const licAssigned = licenses.reduce((a, l) => a + (l.assigned || 0), 0);

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center px-5 sm:px-8 bg-zinc-950/60 backdrop-blur lg:sticky lg:top-0 z-10">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2">
          <span className="text-zinc-600">~/</span> site 360 <ChevronRight className="h-3 w-3" /> single pane
        </span>
      </header>

      <div className="p-5 sm:p-8 max-w-6xl">
        <div className="no-print flex justify-end mb-4"><PrintButton /></div>
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-blue-400 font-semibold">
          <LayoutPanelLeft className="h-4 w-4" /> Site 360
        </div>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mt-2">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-white tracking-tight">Single Pane of Glass</h1>
            <p className="text-zinc-400 mt-3 max-w-2xl">
              Pick a location to see its migration status, emergency-calling compliance, room endpoints —
              plus estate-wide license and certificate health — on one screen.
            </p>
          </div>
          <div className="shrink-0">
            <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold block mb-1.5">Location</label>
            <Select value={selId} onValueChange={setSelId}>
              <SelectTrigger data-testid="site360-select" className="w-56 h-10 bg-zinc-900 border-zinc-800 text-sm"><SelectValue placeholder="Select a site" /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {!site ? (
          <div className="mt-8 text-zinc-500 font-mono text-sm">Loading site data…</div>
        ) : (
          <div className="mt-6 grid md:grid-cols-2 gap-4" data-testid="site360-grid">
            {/* Migration */}
            <Card title="Migration" icon={Cloud}>
              <div className="flex items-center gap-2">
                <span className="text-lg text-white font-semibold">{site.name}</span>
                <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full border ${MIG_STATUS[site.status]?.cls || ""}`}>{MIG_STATUS[site.status]?.label || site.status}</span>
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-[13px] text-zinc-400">
                <span><span className="text-zinc-600">Carrier:</span> {site.carrier || "—"}</span>
                <span><span className="text-zinc-600">Circuits:</span> <span className="font-mono">{site.circuit_ids || "—"}</span></span>
                <span><span className="text-zinc-600">FOC:</span> {site.foc_date || "—"}</span>
                <span><span className="text-zinc-600">Users:</span> {site.users ?? "—"}</span>
              </div>
              {site.components && Object.values(site.components).some(Boolean) && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {Object.entries(site.components).filter(([, v]) => v).map(([k]) => (
                    <span key={k} className="text-[11px] px-2 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 capitalize">{k} ✓</span>
                  ))}
                </div>
              )}
              {site.notes && <p className="text-[12px] text-zinc-500 mt-2">{site.notes}</p>}
            </Card>

            {/* E911 */}
            <Card title="E911 Compliance" icon={Siren}>
              {siteE911 ? (
                <div data-testid="site360-e911">
                  <div className={`text-lg font-semibold ${E911_STATUS[siteE911.status]?.cls || "text-white"}`}>
                    {E911_STATUS[siteE911.status]?.label || siteE911.status}
                  </div>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-[13px] text-zinc-400 font-mono">
                    <span><span className="text-zinc-600">ERL:</span> {siteE911.erl || "—"}</span>
                    <span><span className={siteE911.elin ? "text-zinc-600" : "text-amber-500"}>ELIN:</span> {siteE911.elin || "— missing"}</span>
                  </div>
                  <div className="text-[13px] text-zinc-400 mt-1"><span className="text-zinc-600">Location:</span> {siteE911.location || "—"}</div>
                </div>
              ) : (
                <div className="text-[13px] text-zinc-500">No E911 record matched for this site.</div>
              )}
            </Card>

            {/* Rooms */}
            <Card title={`Room Endpoints (${siteRooms.length})`} icon={MonitorPlay}>
              {siteRooms.length === 0 ? (
                <div className="text-[13px] text-zinc-500">No room devices tagged to this location.</div>
              ) : (
                <div className="space-y-2" data-testid="site360-rooms">
                  {siteRooms.map((r) => (
                    <div key={r.id} className="flex items-center justify-between rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2">
                      <div>
                        <div className="text-[13px] text-white">{r.name}</div>
                        <div className="text-[11px] text-zinc-500">{r.model} · {r.platform}</div>
                      </div>
                      <span className={`text-[12px] font-semibold capitalize ${ROOM_STATUS[r.status] || "text-zinc-400"}`}>{r.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Estate context: certs + licenses */}
            <Card title="Estate Health" icon={ShieldAlert}>
              <div className="grid grid-cols-2 gap-3">
                <MiniStat label="Certs expired" value={certExpired} tone={certExpired > 0 ? "red" : "default"} testId="site360-cert-expired" />
                <MiniStat label="Certs ≤90d" value={certSoon} tone={certSoon > 0 ? "amber" : "default"} testId="site360-cert-soon" />
                <MiniStat label="Lic. entitled" value={licEntitled} />
                <MiniStat label="Lic. assigned" value={licAssigned} tone={licAssigned > licEntitled ? "red" : "default"} />
              </div>
              <div className="flex items-center gap-1.5 mt-3 text-[11px] text-zinc-600">
                <KeyRound className="h-3 w-3" /> Licenses &amp; certs are tracked estate-wide, not per site.
              </div>
            </Card>

            {/* Cloud Readiness scoring */}
            <div className="md:col-span-2 rounded-lg border border-zinc-800 bg-zinc-900 p-5" data-testid="site360-readiness">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-emerald-400 font-semibold mb-3">
                <Gauge className="h-4 w-4" /> Cloud Readiness — {site.name}
              </div>
              {cloudChecks.length === 0 ? (
                <div className="text-[13px] text-zinc-500">Loading readiness checks…</div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                  {cloudChecks.map((chk) => {
                    const sc = scoreFor(chk.id, chk.steps);
                    const open = openCheck === chk.id;
                    const pctCls = sc.pct === null ? "text-zinc-500" : sc.pct >= 80 ? "text-emerald-400" : sc.pct >= 50 ? "text-amber-400" : "text-red-400";
                    return (
                      <div key={chk.id} data-testid={`readiness-${chk.id}`} className="rounded-md border border-zinc-800 bg-zinc-950 overflow-hidden">
                        <button onClick={() => setOpenCheck(open ? null : chk.id)} data-testid={`readiness-toggle-${chk.id}`} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-zinc-800/40 transition-colors">
                          <div className="min-w-0">
                            <div className="text-[13px] text-white font-medium truncate">{chk.title}</div>
                            <div className="text-[11px] text-zinc-500">{sc.scored}/{sc.total} steps assessed</div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span data-testid={`readiness-score-${chk.id}`} className={`font-heading text-2xl font-extrabold ${pctCls}`}>{sc.pct === null ? "—" : `${sc.pct}%`}</span>
                            <ChevronDown className={`h-4 w-4 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
                          </div>
                        </button>
                        {/* progress bar */}
                        <div className="h-1 bg-zinc-800">
                          <div className={`h-full ${sc.pct === null ? "" : sc.pct >= 80 ? "bg-emerald-500" : sc.pct >= 50 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${sc.pct || 0}%` }} />
                        </div>
                        {open && (
                          <div className="p-3 space-y-2 border-t border-zinc-800">
                            {chk.steps.map((s) => {
                              const status = (readiness[chk.id] || {})[s.id];
                              return (
                                <div key={s.id} className="flex items-center justify-between gap-2">
                                  <span className="text-[12px] text-zinc-300 truncate flex-1">{s.title}</span>
                                  <div className="flex items-center gap-1 shrink-0">
                                    {["pass", "fail", "na"].map((v) => {
                                      const active = status === v || (!status && v === "na");
                                      const cls = v === "pass"
                                        ? (status === "pass" ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/40" : "text-zinc-500 border-zinc-700")
                                        : v === "fail"
                                        ? (status === "fail" ? "bg-red-500/20 text-red-400 border-red-500/40" : "text-zinc-500 border-zinc-700")
                                        : (!status || status === "na" ? "bg-zinc-700/40 text-zinc-300 border-zinc-600" : "text-zinc-500 border-zinc-700");
                                      return (
                                        <button
                                          key={v}
                                          onClick={() => mark(chk.id, s.id, v)}
                                          data-testid={`readiness-${chk.id}-${s.id}-${v}`}
                                          className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded border transition-colors ${cls}`}
                                        >
                                          {v === "na" ? "N/A" : v}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Card = ({ title, icon: Icon, children }) => (
  <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-5">
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-blue-400 font-semibold mb-3">
      {Icon && <Icon className="h-4 w-4" />} {title}
    </div>
    {children}
  </div>
);

const MiniStat = ({ label, value, tone = "default", testId }) => {
  const cls = tone === "red" ? "text-red-400" : tone === "amber" ? "text-amber-400" : "text-white";
  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3" data-testid={testId}>
      <div className={`font-heading text-2xl font-extrabold ${cls}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mt-1">{label}</div>
    </div>
  );
};
